import { AsyncPipe } from '@angular/common';
import { Component, inject } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router';
import { I18NextAdapterPipe, I18nService } from '@pflegix/i18n';
import { BehaviorSubject } from 'rxjs';
import { filter } from 'rxjs/operators';
import { BusinessPartnerStore } from '../feature/business-partner-management/store';
import { BUSINESS_PARTNER_CENTER_TRANSLATIONS_DE } from '../view/root/children/business-partner-center/i18n/de/business-partner-center.translations.de';
import { BUSINESS_PARTNER_CENTER_TRANSLATIONS_EN } from '../view/root/children/business-partner-center/i18n/en/business-partner-center.translations.en';

interface Breadcrumb {
  label: string;
  url?: string;
}

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'app-breadcrumb',
  templateUrl: './app.breadcrumb.component.html',
  imports: [AsyncPipe, I18NextAdapterPipe],
  styles: `
    .layout-breadcrumb-container {
      height: 100%;
      box-shadow: none;
    }
  `,
})
export class AppBreadcrumbComponent {
  private router = inject(Router);
  private titleService = inject(Title);
  private i18n = inject(I18nService);

  private readonly _breadcrumbs$ = new BehaviorSubject<Breadcrumb[]>([]);

  protected readonly breadcrumbs$ = this._breadcrumbs$.asObservable();

  private businessPartnerStore = inject(BusinessPartnerStore);

  constructor() {
    this.i18n.addTranslations({
      namespace: 'business-partner-center',
      de: BUSINESS_PARTNER_CENTER_TRANSLATIONS_DE,
      en: BUSINESS_PARTNER_CENTER_TRANSLATIONS_EN,
    });

    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe((event) => {
      const { root } = this.router.routerState.snapshot;
      const breadcrumbs: Breadcrumb[] = [];

      this.addBreadcrumb(root, [], breadcrumbs);

      const lastBreadCrumb = breadcrumbs[breadcrumbs.length - 1];
      const titlePrefix = this.setTitlePrefixForEntity(root);

      this.titleService.setTitle(`${titlePrefix}${this.i18n.t(lastBreadCrumb.label)}`);

      this._breadcrumbs$.next(breadcrumbs);
    });
  }

  private addBreadcrumb(route: ActivatedRouteSnapshot, parentUrl: string[], breadcrumbs: Breadcrumb[]): void {
    const routeUrl = parentUrl.concat(route.url.map((url) => url.path));
    const { breadcrumb } = route.data;
    const parentBreadcrumb = route.parent && route.parent.data ? route.parent.data['breadcrumb'] : null;

    if (breadcrumb && breadcrumb !== parentBreadcrumb) {
      breadcrumbs.push({
        label: route.data['breadcrumb'],
        url: `/${routeUrl.join('/')}`,
      });
    }

    if (route.firstChild) {
      this.addBreadcrumb(route.firstChild, routeUrl, breadcrumbs);
    }
  }

  private setTitlePrefixForEntity(root: ActivatedRouteSnapshot): string {
    const uuid = this.extractUuidFromRouteProperty(root, 'businessPartnerId');

    if (uuid && this.businessPartnerStore.entityMap()[uuid]) {
      const businessPartner = this.businessPartnerStore.entityMap()[uuid];

      return `${businessPartner.mainAddress.lastname}, ${businessPartner.mainAddress.firstname} - `;
    }
    return '';
  }

  private extractUuidFromRouteProperty(route: ActivatedRouteSnapshot, propertyName: string): string | null {
    if (route.params && route.params[propertyName]) {
      return route.params[propertyName];
    }
    if (route.firstChild) {
      return this.extractUuidFromRouteProperty(route.firstChild, propertyName);
    }
    return null;
  }
}
